import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@src/store/base-query';

export const helpApi = createApi({
  reducerPath: 'helpApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Faqs', 'BugReports', 'Suggestions'],
  endpoints: (builder) => ({
    // ─── FAQs ───────────────────────────────────────────────────────────────
    getFaqs: builder.query({
      query: () => ({ url: 'help/faqs', method: 'GET' }),
      providesTags: ['Faqs'],
    }),
    getAllFaqs: builder.query({
      query: () => ({ url: 'help/faqs/all', method: 'GET' }),
      providesTags: ['Faqs'],
    }),
    createFaq: builder.mutation({
      query: (body) => ({ url: 'help/faqs', method: 'POST', body }),
      invalidatesTags: ['Faqs'],
    }),
    updateFaq: builder.mutation({
      query: ({ id, ...body }) => ({ url: `help/faqs/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Faqs'],
    }),
    deleteFaq: builder.mutation({
      query: (id) => ({ url: `help/faqs/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Faqs'],
    }),

    // ─── Bug Reports ─────────────────────────────────────────────────────────
    createBugReport: builder.mutation({
      query: ({ title, description, category, screenshots = [] }) => {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);

        if (category) formData.append('category', category);

        screenshots.forEach((file) => formData.append('screenshots', file));

        return { url: 'help/bug-reports', method: 'POST', body: formData };
      },
      invalidatesTags: ['BugReports'],
    }),
    getMyBugReports: builder.query({
      query: () => ({ url: 'help/bug-reports/my', method: 'GET' }),
      providesTags: ['BugReports'],
    }),
    getAllBugReports: builder.query({
      query: ({ page = 1, limit = 20, status } = {}) => ({
        url: 'help/bug-reports',
        method: 'GET',
        params: { page, limit, ...(status && { status }) },
      }),
      providesTags: ['BugReports'],
    }),
    updateBugReport: builder.mutation({
      query: ({ id, ...body }) => ({ url: `help/bug-reports/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['BugReports'],
    }),
    deleteMyBugReport: builder.mutation({
      query: (id) => ({ url: `help/bug-reports/my/${id}`, method: 'DELETE' }),
      invalidatesTags: ['BugReports'],
    }),
    deleteBugReport: builder.mutation({
      query: (id) => ({ url: `help/bug-reports/${id}`, method: 'DELETE' }),
      invalidatesTags: ['BugReports'],
    }),

    // ─── Suggestions ─────────────────────────────────────────────────────────
    createSuggestion: builder.mutation({
      query: (body) => ({ url: 'help/suggestions', method: 'POST', body }),
      invalidatesTags: ['Suggestions'],
    }),
    getMySuggestions: builder.query({
      query: () => ({ url: 'help/suggestions/my', method: 'GET' }),
      providesTags: ['Suggestions'],
    }),
    getAllSuggestions: builder.query({
      query: ({ page = 1, limit = 20, status } = {}) => ({
        url: 'help/suggestions',
        method: 'GET',
        params: { page, limit, ...(status && { status }) },
      }),
      providesTags: ['Suggestions'],
    }),
    toggleUpvote: builder.mutation({
      query: (id) => ({ url: `help/suggestions/${id}/upvote`, method: 'POST' }),
      invalidatesTags: ['Suggestions'],
    }),
    updateSuggestion: builder.mutation({
      query: ({ id, ...body }) => ({ url: `help/suggestions/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Suggestions'],
    }),
    deleteMySuggestion: builder.mutation({
      query: (id) => ({ url: `help/suggestions/my/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Suggestions'],
    }),
    deleteSuggestion: builder.mutation({
      query: (id) => ({ url: `help/suggestions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Suggestions'],
    }),
  }),
});

export const {
  useGetFaqsQuery,
  useGetAllFaqsQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useCreateBugReportMutation,
  useGetMyBugReportsQuery,
  useGetAllBugReportsQuery,
  useUpdateBugReportMutation,
  useDeleteMyBugReportMutation,
  useDeleteBugReportMutation,
  useCreateSuggestionMutation,
  useGetMySuggestionsQuery,
  useGetAllSuggestionsQuery,
  useToggleUpvoteMutation,
  useUpdateSuggestionMutation,
  useDeleteMySuggestionMutation,
  useDeleteSuggestionMutation,
} = helpApi;
